import React, { useContext, useEffect, useState } from 'react'
import {
    Typography,
    Stack,
    Box,
    CircularProgress,
    Button,
    Alert,
    Snackbar,
    Divider,
    FormControlLabel,
    Checkbox,
    Card,
    Modal,
    Paper
} from '@mui/material'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import SubmitSvg from '../SubmitShift/SubmitSvg'
import { isMobile } from '../../Utilities/isMobile'
import { useParams } from 'react-router-dom'
import EditStores from './EditFlows/EditStores'
import { PATHS } from '../../constants'
import {
    getTextField,
    getStoreTextField,
    getNumberField,
    getEmploymentTypeSelect,
    EmploymentCard,
    AdminManagerButtons,
    handleSave,
    fetchUserData,
    useEditingState,
    getEmpCdField
} from '../../Utilities/userManagementUtils'

const UserManagementEdit = ({ history }) => {
    const { userEmpId, userId } = useParams()
    const employmentId = localStorage.getItem('selected_emp')
    const { isAdmin, isManager, userEmployments, fetchAndSetEmployments } = useContext(EmploymentsContext)
    const [info, setInfo] = useState()
    const [originalInfo, setOriginalInfo] = useState()
    const [employments, setEmployments] = useState()
    const [originalEmployments, setOriginalEmployments] = useState()
    const [editing, setEditing] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [errors, setErrors] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [newPass, setNewPass] = useState(null)
    const [storeList, setStoreList] = useState([])
    const [openEditStores, setOpenEditStores] = useState(false)
    const fetch = async () => {
        await fetchUserData(userId, userEmpId, setInfo, setOriginalInfo, setEmployments, setOriginalEmployments, setStoreList)
    }

    const canEditTargetUser = () => {
        // Admins can edit all users
        if (isAdmin) return true
        
        // Non-managers cannot edit other users' data
        if (!isManager) return false
        
        // Get stores where current user is a manager
        const managedStores = userEmployments
            ?.filter(emp => emp.is_manager)
            ?.map(emp => emp.store_id) || []
        
        // Get stores where target user works
        const targetUserStores = employments?.map(emp => emp.store_id) || []
        
        // Check if there's any overlap
        return managedStores.some(storeId => targetUserStores.includes(storeId))
    }

    const handleSaveClick = () => {
        handleSave(employments, originalEmployments, info, originalInfo, errors, setErrors, setSnackbarMessage, userId, handleReturn)
    }

    const handleReturn = () => {
        history.push(PATHS.managerSettingsUsers)
    }

    useEditingState(info, originalInfo, employments, originalEmployments, setEditing)

    useEffect(() => {
        fetch()
    }, [])

    if (!info) {
        return <CircularProgress sx={{ mx: '50%', mt: '10%' }} />
    }

    if (newPass)
        return (
            <div
                style={{
                    height: 'calc(100vh - 56px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    margin: '0 1rem'
                }}
            >
                <SubmitSvg />
                <Typography variant="header1" sx={{ fontSize: '20px' }}>
                    新しいパスワード: {newPass}
                </Typography>
                <Button
                    onClick={() => {
                        setNewPass(null)
                    }}
                    variant="contained"
                    color="paloBlue"
                    fullWidth
                    sx={{ mt: 'auto', mb: '2rem' }}
                >
                    完了
                </Button>
            </div>
        )
    return (
        <div>
            <Box className="input-box shrinkable">
                <Snackbar
                    open={snackbarMessage !== ''}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarMessage('')}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbarMessage('')}>{snackbarMessage}</Alert>
                </Snackbar>
            </Box>
            {!info.confirmed_at && <Alert severity="error">メールアドレスの本人確認が未完了です。</Alert>}
            {info.deactivated_at && <Alert severity="error">アカウントが無効になっています。</Alert>}
            <Stack direction={isMobile() ? "column" : "row"} spacing={2} sx={{ mt: '1rem' }}>
                <Card sx={{ p: isMobile() ? 4 : 2, width: isMobile() ? '90vw' : '48%', boxShadow: isMobile() && 'none'}} >
                    <Typography variant="h6" sx={{ mb: 2 }}>基本情報</Typography>
                    <Stack spacing={2} sx={{ flexWrap: 'wrap'}}>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('せい(かな)', 'name_last', info, setInfo, errors, setErrors)}
                            {getTextField('めい(かな)', 'name_first', info, setInfo, errors, setErrors)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('姓(漢字)', 'kanji_last', info, setInfo, errors, setErrors)}
                            {getTextField('名(漢字)', 'kanji_first', info, setInfo, errors, setErrors)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('ミドルネーム', 'name_middle', info, setInfo, errors, setErrors)}
                            {getTextField('ニックネーム', 'nickname', info, setInfo, errors, setErrors)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('メールアドレス', 'email', info, setInfo, errors, setErrors)}
                            {getTextField('電話番号', 'phone', info, setInfo, errors, setErrors)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getStoreTextField('店舗情報', 'store_ids', info, storeList, setOpenEditStores)} 
                            {getEmpCdField(info, setInfo, errors, setErrors, employments, storeList)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getEmploymentTypeSelect(info, setInfo, canEditTargetUser)}
                            {getTextField('時給', 'hourly_wage', info, setInfo, errors, setErrors, !canEditTargetUser())}
                        </Stack>
                        {getNumberField('最大労働時間', 'monthly_max_hours', info, setInfo, !canEditTargetUser())}
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        style={{ marginLeft: '-0.75rem' }}
                                        checked={info.view_only}
                                        onChange={e => setInfo({ ...info, view_only: e.target.checked })}
                                    />
                                }
                                label="正社員(間接)"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        style={{ marginLeft: '-0.75rem' }}
                                        checked={info.is_student}
                                        onChange={e => setInfo({ ...info, is_student: e.target.checked })}
                                    />
                                }
                                label="高校生"
                            />
                        </Stack>
                        <AdminManagerButtons 
                            info={info}
                            userId={userId}
                            setSnackbarMessage={setSnackbarMessage}
                            fetch={fetch}
                            setNewPass={setNewPass}
                            setIsLoading={setIsLoading}
                            history={history}
                            employmentId={employmentId}
                            isAdmin={isAdmin}
                        />
                    </Stack>
                </Card>
                <Box style={{ width: isMobile() ? '90vw' : '45%', marginBottom: isMobile() && '4rem' }}>
                    {employments.length ? employments.map(e => {
                        return (
                            <Card key={e.id} sx={{ p: isMobile() ? 3 : 2, width: isMobile() ? '90vw' : '100%', boxShadow: isMobile() && 'none'}} >
                                <EmploymentCard 
                                    employment={e}
                                    employments={employments}
                                    setEmployments={setEmployments}
                                    info={info}
                                    setSnackbarMessage={setSnackbarMessage}
                                />
                            </Card>
                        )
                    }) : <Typography>所属店舗情報がありません。</Typography>}
                </Box>
            </Stack>
            <Box style={{ backgroundColor: 'white', width: '100%', position: 'fixed', bottom: 0, marginLeft: '-1rem', zIndex: isMobile() && 1001, marginBottom: isMobile() && '3rem' }}>
                <Divider />
                <Box style={{ display: 'flex', justifyContent: 'left', margin: '1rem' }}>
                    <Button variant='contained' sx={{ ml: 2 }} onClick={handleSaveClick} disabled={!editing}>
                        変更して保存する
                    </Button>
                    <Button variant='outlined' sx={{ ml: 2 }} onClick={handleReturn}>
                        キャンセル
                    </Button>
                </Box>
                {isLoading && <CircularProgress sx={{ mx: '50%', mt: '10%' }} />}
                {openEditStores && <Modal open={openEditStores} onClose={() => setOpenEditStores(false)}>
                    <Paper sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90vw',
                            p: 2,
                            borderRadius: '10px',
                            height: 'auto',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                    }}>
                        <EditStores
                            current={employments}
                            handleReturn={() => {fetch(); setOpenEditStores(false)}}
                            employmentInfo={info}
                            fetchAndSetEmployments={fetchAndSetEmployments}
                            history={history}
                            titleOnly={true}
                        />
                    </Paper>
                </Modal>}
            </Box>
        </div>
    )
}

export default UserManagementEdit
